import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errText, fmt } from "@/lib/api";
import { Meter, Chip, Loading } from "@/console/ui";

export function Watermark({ text }) {
  const rows = Array.from({ length: 14 });
  return (
    <div className="wm-layer" data-testid="report-watermark-overlay">
      {rows.map((_, i) => (
        <div
          key={i}
          className="font-data text-xs whitespace-nowrap text-[#7B2617]"
          style={{ transform: "rotate(-24deg)", marginTop: 62, letterSpacing: 3 }}
        >
          {Array.from({ length: 8 })
            .map(() => `${text} · ${new Date().toISOString().slice(0, 10)}`)
            .join("   ")}
        </div>
      ))}
    </div>
  );
}

export function IntegrityBlock({ data }) {
  const g = data.integrity;
  if (!g) return null;
  const secs = (ms) => `${Math.round((ms || 0) / 1000)}s`;
  const stats = [
    ["Tab departures", g.departures],
    ["Total time away", secs(g.total_away_ms)],
    ["Longest absence", secs(g.longest_away_ms)],
    ["Paste events", g.paste_events],
    ["Copy events", g.copy_events],
  ];
  return (
    <div
      className="mt-8 border border-[#E7DED7] rounded-md bg-white p-4"
      data-testid="report-integrity"
    >
      <h2 className="font-console text-sm font-medium">Answer-integrity signals (advisory)</h2>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3">
        {stats.map(([label, v]) => (
          <div key={label} className="border border-[#E7DED7] rounded p-2.5 bg-[#FBF8F5]">
            <div className="text-[10px] uppercase tracking-wider text-[#8C7F78]">{label}</div>
            <div className="font-data text-base mt-0.5">{v}</div>
          </div>
        ))}
      </div>

      {g.answers?.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs min-w-[620px]" data-testid="integrity-answers-table">
            <thead className="bg-[#F6F1EC] text-[#7A6D66]">
              <tr>
                {["Answer", "Characters", "Typed", "Pasted", "Paste share", "Keystrokes", "Chars/min"].map(
                  (h) => (
                    <th key={h} className="text-left font-medium px-2.5 py-2">
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {g.answers.map((r) => (
                <tr
                  key={r.item_id}
                  className="border-t border-[#E7DED7]"
                  data-testid={`integrity-row-${r.item_id}`}
                >
                  <td className="px-2.5 py-2 font-data">{r.item_id}</td>
                  <td className="px-2.5 py-2 font-data">{r.final_chars}</td>
                  <td className="px-2.5 py-2 font-data">{r.typed_chars}</td>
                  <td className="px-2.5 py-2 font-data">
                    {r.paste_chars} ({r.paste_count}x)
                  </td>
                  <td
                    className={`px-2.5 py-2 font-data ${
                      r.paste_share >= 0.4 ? "text-rose-700 font-medium" : ""
                    }`}
                  >
                    {Math.round(r.paste_share * 100)}%
                  </td>
                  <td className="px-2.5 py-2 font-data">{r.keystrokes}</td>
                  <td className="px-2.5 py-2 font-data">{r.chars_per_minute ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {g.notes?.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="integrity-notes">
          {g.notes.map((n) => (
            <li key={n} className="text-[11px] text-[#4A3F3A]">
              · {n}
            </li>
          ))}
        </ul>
      )}
      <p className="text-[10px] text-[#8C7F78] mt-3">{g.caveat}</p>
    </div>
  );
}

export function ProctoringBlock({ data, snapshotUrl }) {
  const p = data.proctoring;
  if (!p) return null;
  return (
    <div className="mt-8 border border-[#E7DED7] rounded-md bg-white p-4" data-testid="report-proctoring">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-console text-sm font-medium">Proctoring verification</h2>
        {p.interruptions > 0 ? (
          <Chip
            label={`${p.interruptions} camera interruption(s)`}
            tone="bg-rose-100 text-rose-800"
            testId="report-proctor-interruptions"
          />
        ) : (
          <Chip
            label="no camera interruptions"
            tone="bg-emerald-100 text-emerald-800"
            testId="report-proctor-clean"
          />
        )}
      </div>
      <p className="text-[11px] text-[#6B5F59] mt-2" data-testid="report-proctor-line">
        {p.required
          ? "A working camera was required for this assessment. "
          : "Camera proctoring was optional for this assessment. "}
        {p.captured} still photograph{p.captured === 1 ? "" : "s"} captured
        {p.expected ? ` of ${p.expected} expected` : ""}.
        {p.verified_by
          ? ` Proctoring verified by ${p.verified_by}${p.verified_at ? ` on ${fmt(p.verified_at)}` : ""}.`
          : " Not yet verified by a reviewer."}
      </p>
      {p.snapshots?.length > 0 ? (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="report-proctor-strip">
          {p.snapshots.map((s) => (
            <figure key={s.id} className="border border-[#DCD1C9] rounded overflow-hidden">
              <img
                src={snapshotUrl(s.id)}
                alt={`Proctoring photograph ${s.sequence + 1}`}
                className="w-28 h-20 object-cover"
                data-testid={`report-proctor-photo-${s.id}`}
              />
              <figcaption className="text-[9px] font-data text-[#7A6D66] px-1 py-0.5">
                {fmt(s.at).split(", ")[1] || fmt(s.at)}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-[#7A6D66] mt-3" data-testid="report-proctor-none">
          No photographs were captured for this assessment.
        </p>
      )}
      <p className="text-[10px] text-[#8C7F78] mt-3">
        Photographs confirm that the invited person took the assessment. They are not scored, and no
        face recognition or emotion analysis is performed on them.
      </p>
    </div>
  );
}

function duration(from, to) {
  if (!from || !to) return "—";
  const mins = Math.round((new Date(to) - new Date(from)) / 60000);
  if (!Number.isFinite(mins)) return "—";
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function TimingBlock({ data }) {
  const t = data.timing || {};
  const c = data.consent;
  const cells = [
    ["Reference", t.reference || "—"],
    ["Started", fmt(t.started_at)],
    ["Submitted", fmt(t.submitted_at)],
    ["Time taken", duration(t.started_at, t.submitted_at)],
    ["Time limit", t.time_limit_minutes ? `${t.time_limit_minutes} min` : "—"],
    ["Auto-submitted", t.auto_submitted ? "yes" : "no"],
  ];
  return (
    <div className="mt-8 border border-[#E7DED7] rounded-md bg-white p-4" data-testid="report-timing">
      <h2 className="font-console text-sm font-medium">Administration record</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3">
        {cells.map(([label, v]) => (
          <div key={label} className="border border-[#E7DED7] rounded p-2.5 bg-[#FBF8F5] min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#8C7F78]">{label}</div>
            <div className="font-data text-xs mt-0.5 break-words">{v}</div>
          </div>
        ))}
      </div>
      {c && (
        <p className="text-[11px] text-[#6B5F59] mt-3" data-testid="report-consent-line">
          Consent given by <strong>{c.full_name || "—"}</strong> at {fmt(c.at)}
          {c.camera_confirmed ? " · camera confirmed" : ""}
        </p>
      )}
      {data.proctor_events?.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[11px] min-w-[420px]" data-testid="report-camera-events">
            <thead className="bg-[#F6F1EC] text-[#7A6D66]">
              <tr>
                {["When", "Event", "Detail"].map((h) => (
                  <th key={h} className="text-left font-medium px-2.5 py-1.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.proctor_events.map((e, i) => (
                <tr key={`${e.code}-${e.at}-${i}`} className="border-t border-[#E7DED7]">
                  <td className="px-2.5 py-1.5 font-data">{fmt(e.at)}</td>
                  <td className="px-2.5 py-1.5 font-data">{e.code}</td>
                  <td className="px-2.5 py-1.5">{e.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScoreCard({ sc }) {
  const meta = [
    ["Machine proposal", sc.ai_score ?? "—"],
    ["Confidence", sc.confidence ?? "—"],
    ["Source", sc.source],
    ["Reviewer", sc.reviewed_by_email || "—"],
    ["Rubric / prompt", `${sc.rubric_version || "—"}${sc.prompt_version ? ` · ${sc.prompt_version}` : ""}`],
    ["Model", sc.model || "—"],
  ];
  return (
    <div
      className="mt-3 border border-[#E7DED7] rounded bg-[#FBF8F5] p-3"
      data-testid={`detail-score-${sc.indicator}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-console text-xs font-medium">{sc.indicator_name}</span>
        <span className="font-data text-sm">{sc.final_score}</span>
        {sc.locked && <Chip label="locked" tone="bg-[#E7DED7] text-[#332924]" />}
        <Chip
          label={sc.source}
          tone={sc.source === "manual" ? "bg-pink-100 text-pink-800" : "bg-[#F3E4E0] text-[#7B2617]"}
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
        {meta.map(([label, v]) => (
          <div key={label} className="min-w-0">
            <div className="text-[9px] uppercase tracking-wider text-[#8C7F78]">{label}</div>
            <div className="font-data text-[11px] break-words">{v}</div>
          </div>
        ))}
      </div>
      {sc.rationale && (
        <p className="text-[11px] text-[#4A3F3A] mt-2 leading-relaxed">
          <span className="text-[#8C7F78]">Rationale: </span>
          {sc.rationale}
        </p>
      )}
      {sc.override_reason && (
        <p className="text-[11px] text-[#7B2617] mt-1.5 leading-relaxed">
          <span className="text-[#8C7F78]">Reviewer override reason: </span>
          {sc.override_reason}
        </p>
      )}
      {sc.rubric?.levels?.length > 0 && (
        <div className="mt-2.5 overflow-x-auto">
          <table className="w-full text-[11px] min-w-[420px]">
            <thead className="text-[#7A6D66]">
              <tr>
                {["Level", "Label", "Rubric descriptor"].map((h) => (
                  <th key={h} className="text-left font-medium px-2 py-1">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sc.rubric.levels.map((lv) => {
                const hit = sc.level != null && lv.level === sc.level;
                return (
                  <tr
                    key={lv.level}
                    className={`border-t border-[#E7DED7] ${hit ? "bg-[#F6EBE6]" : ""}`}
                  >
                    <td className={`px-2 py-1 font-data ${hit ? "font-semibold" : ""}`}>{lv.level}</td>
                    <td className={`px-2 py-1 ${hit ? "font-semibold" : ""}`}>{lv.label}</td>
                    <td className="px-2 py-1 text-[#4A3F3A]">{lv.descriptor}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function FullDetailBlock({ data }) {
  const sections = data.full_detail;
  if (!sections?.length) return null;
  return (
    <div className="mt-10" data-testid="report-full-detail">
      <h2 className="font-console text-lg md:text-lg font-medium">
        Full response detail, rubrics and scoring decisions
      </h2>
      <p className="text-[11px] text-[#6B5F59] mt-1 mb-4 max-w-2xl leading-relaxed">
        Every question exactly as the candidate saw it, their answer in their own words, the rubric
        applied, the machine&apos;s proposal and rationale, and the reviewer&apos;s decision.
      </p>
      {sections.map((s) => (
        <details
          key={s.key}
          open
          className="border border-[#E7DED7] rounded-md bg-white mb-4"
          data-testid={`detail-section-${s.key}`}
        >
          <summary className="cursor-pointer px-4 py-3 select-none">
            <span className="font-console text-sm font-medium">{s.title}</span>
            <span className="font-data text-[11px] text-[#7A6D66] ml-2">
              {s.type} · weight {s.weight} · {s.items.length} item{s.items.length === 1 ? "" : "s"}
            </span>
          </summary>
          <div className="px-4 pb-4">
            {s.instructions && (
              <p className="text-[11px] text-[#6B5F59] leading-relaxed">{s.instructions}</p>
            )}
            {s.items.map((item, idx) => (
              <div
                key={item.item_id}
                className="mt-4 pt-4 border-t border-[#E7DED7]"
                data-testid={`detail-item-${item.item_id}`}
              >
                <p className="font-data text-[10px] uppercase tracking-wider text-[#8C7F78]">
                  Item {idx + 1} · {item.item_id}
                  {item.facet ? ` · ${item.facet}` : ""}
                  {item.min_words ? ` · min ${item.min_words} words` : ""}
                </p>
                {item.prompts.map((p) => (
                  <div key={p.label} className="mt-2">
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7F78]">
                      {p.label}
                    </div>
                    <p className="text-[13px] text-[#241A16] leading-relaxed whitespace-pre-line">
                      {p.text}
                    </p>
                  </div>
                ))}
                {item.answers.map((a) => (
                  <div
                    key={a.label}
                    className="mt-2 border-l-2 border-[#7B2617] pl-3"
                    data-testid={`detail-answer-${item.item_id}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7F78]">
                      {a.label}
                    </div>
                    <p className="text-[13px] text-[#3A2E28] leading-relaxed whitespace-pre-line">
                      {a.text}
                    </p>
                  </div>
                ))}
                {item.scores.map((sc) => (
                  <ScoreCard key={`${item.item_id}-${sc.indicator}`} sc={sc} />
                ))}
                {item.integrity_checks?.map((ic) => (
                  <p
                    key={ic.indicator}
                    className="text-[11px] text-[#7A6D66] mt-2"
                    data-testid={`detail-integrity-${item.item_id}`}
                  >
                    Integrity check · {ic.final_score}
                    {ic.rationale ? ` — ${ic.rationale}` : ""}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

export function ReportBody({ data, snapshotUrl }) {
  return (
    <div className="relative">
      <Watermark text={data.watermark} />
      <div className="relative z-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-data text-[11px] text-[#7A6D66] uppercase tracking-widest">
              Employer report · confidential
            </p>
            <h1 className="font-console text-2xl sm:text-3xl font-semibold tracking-tight mt-1" data-testid="report-candidate-name">
              {data.candidate.name}
            </h1>
            <p className="text-xs text-[#6B5F59] mt-1">
              {data.role_applied} · {data.battery_name} · submitted {fmt(data.submitted_at)}
            </p>
          </div>
          <div className="text-right">
            <div className="font-data text-5xl leading-none" data-testid="report-composite">
              {data.composite.composite}
            </div>
            <div className="font-console text-sm mt-1" data-testid="report-band">
              Band {data.composite.band} — {data.composite.band_label}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <Meter value={data.composite.composite} />
          <div className="flex justify-between mt-1.5 font-data text-[10px] text-[#7A6D66]">
            {data.banding
              .slice()
              .sort((a, b) => a.min - b.min)
              .map((b) => (
                <span key={b.band}>
                  {b.band} ≥ {b.min}
                </span>
              ))}
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="report-sections">
          {data.composite.breakdown.map((s) => (
            <div key={s.section_key} className="border border-[#E7DED7] rounded-md bg-white p-4">
              <p className="font-console text-sm font-medium">{s.title}</p>
              <p className="font-data text-[11px] text-[#7A6D66]">
                weight {s.weight} · section score {s.score}
              </p>
              <div className="mt-2">
                <Meter value={s.score} />
              </div>
              <ul className="mt-3 space-y-1.5">
                {s.indicators.map((i) => (
                  <li key={i.indicator} className="text-[11px] flex items-center gap-2">
                    <span className="flex-1 truncate">{i.indicator}</span>
                    <span className="font-data">{i.score}</span>
                    <span className="w-16">
                      <Meter value={i.score} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <h2 className="font-console text-lg md:text-lg font-medium mt-10 mb-3">
          Indicator detail &amp; scoring lineage
        </h2>
        <div className="border border-[#E7DED7] rounded-md overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="report-indicator-table">
            <thead className="bg-white text-[#7A6D66] text-left">
              <tr>
                <th className="px-3 py-2">Indicator</th>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2">Final</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2 hidden sm:table-cell">AI / conf.</th>
                <th className="px-3 py-2 hidden md:table-cell">Rubric</th>
                <th className="px-3 py-2 hidden lg:table-cell">Reviewer</th>
              </tr>
            </thead>
            <tbody>
              {data.indicator_detail.map((r) => (
                <tr
                  key={`${r.section_key}-${r.item_id || ""}-${r.indicator}`}
                  className="border-t border-[#E7DED7]"
                >
                  <td className="px-3 py-2">{r.indicator_name}</td>
                  <td className="px-3 py-2 font-data text-[#7A6D66]">{r.item_id || r.section_key}</td>
                  <td className="px-3 py-2 font-data">{r.score}</td>
                  <td className="px-3 py-2">
                    <Chip
                      label={r.source}
                      tone={
                        r.source === "manual"
                          ? "bg-pink-100 text-pink-800"
                          : r.source === "ai"
                            ? "bg-[#F3E4E0] text-[#7B2617]"
                            : "bg-[#E7DED7] text-[#332924]"
                      }
                    />
                  </td>
                  <td className="px-3 py-2 font-data text-[#7A6D66] hidden sm:table-cell">
                    {r.ai_score ?? "—"} / {r.confidence ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-data text-[#7A6D66] hidden md:table-cell">
                    {r.rubric_version || "—"} {r.prompt_version ? `· ${r.prompt_version}` : ""}
                  </td>
                  <td className="px-3 py-2 font-data text-[#7A6D66] hidden lg:table-cell">
                    {r.reviewed_by_email || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.quality_flags?.length > 0 && (
          <div className="mt-6 border border-amber-200 bg-amber-50 rounded p-3" data-testid="report-flags">
            <p className="font-console text-[11px] uppercase tracking-wider text-amber-700 mb-2">
              Advisory response-quality flags (not scores)
            </p>
            <ul className="text-[11px] font-data text-amber-800 space-y-1">
              {data.quality_flags.map((f) => (
                <li key={`${f.section_key}-${f.item_id || ""}-${f.code}`}>
                  {f.section_key}
                  {f.item_id ? `/${f.item_id}` : ""} · {f.code} — {f.detail}
                </li>
              ))}
            </ul>
          </div>
        )}

        <IntegrityBlock data={data} />
        <ProctoringBlock data={data} snapshotUrl={snapshotUrl} />
        <TimingBlock data={data} />
        <FullDetailBlock data={data} />

        <p className="mt-8 text-[11px] text-[#7A6D66] leading-relaxed border-t border-[#E7DED7] pt-4">
          Every machine-proposed score in this report was confirmed or overridden by a named human
          reviewer before the composite was computed. This report is confidential to the hiring
          organisation and is watermarked with the viewer address {data.watermark}. Do not
          redistribute.
        </p>
      </div>
    </div>
  );
}

export default function ReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [shares, setShares] = useState([]);
  const [logs, setLogs] = useState([]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, s, l] = await Promise.all([
        api.get(`/reports/${id}`),
        api.get(`/reports/${id}/shares`),
        api.get(`/reports/${id}/access-log`),
      ]);
      setData(r.data);
      setShares(s.data);
      setLogs(l.data);
    } catch (e) {
      setError(errText(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data)
    return error ? (
      <div className="h-full flex items-center justify-center text-sm text-[#7A6D66] px-6 text-center" data-testid="report-unavailable">
        {error}
      </div>
    ) : (
      <Loading label="Loading report…" testId="report-loading" />
    );

  return (
    <div className="h-full overflow-y-auto console-scroll p-4 sm:p-6">
      <button className="text-xs text-[#6B5F59] hover:text-[#7B2617] mb-4" data-testid="report-back-button" onClick={() => navigate(`/console/review/${id}`)}>
        ← Back to review
      </button>
      <button
        className="ml-3 text-xs px-3 py-1.5 rounded bg-[#7B2617] hover:bg-[#5E1C10] text-white text-white mb-4"
        data-testid="download-report-pdf-button"
        onClick={() => {
          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${id}/pdf`, "_blank");
        }}
      >
        Download PDF
      </button>

      <ReportBody
        data={data}
        snapshotUrl={(snapId) =>
          `${process.env.REACT_APP_BACKEND_URL}/api/proctor/${id}/snapshot/${snapId}`
        }
      />

      <div className="mt-10 grid gap-4 lg:grid-cols-2">
        <div className="border border-[#E7DED7] rounded-md bg-white p-4">
          <h3 className="font-console text-xs uppercase tracking-wider text-[#6B5F59]">
            External share (7-day, OTP-gated)
          </h3>
          <div className="flex flex-wrap gap-2 mt-3">
            <input
              type="email"
              placeholder="employer viewer email"
              value={email}
              data-testid="share-email-input"
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 min-w-[200px] bg-[#FBF8F5] border border-[#DCD1C9] rounded px-3 py-2 text-xs"
            />
            <button
              className="text-xs px-3 py-2 rounded bg-[#7B2617] hover:bg-[#5E1C10] text-white disabled:opacity-40"
              disabled={!email.includes("@")}
              data-testid="generate-share-link-button"
              onClick={async () => {
                setError("");
                setNotice("");
                try {
                  const { data: s } = await api.post(`/reports/${id}/shares`, { viewer_email: email });
                  setNotice(`Link created for ${s.viewer_email}. It expires ${fmt(s.expires_at)}.`);
                  setEmail("");
                  load();
                } catch (e) {
                  setError(errText(e));
                }
              }}
            >
              Create link
            </button>
          </div>
          {notice && (
            <p className="text-[11px] text-emerald-700 mt-2" data-testid="share-notice">
              {notice}
            </p>
          )}
          {error && (
            <p className="text-[11px] text-rose-700 mt-2" data-testid="share-error">
              {error}
            </p>
          )}
          <ul className="mt-4 space-y-2" data-testid="share-list">
            {shares.length === 0 && <li className="text-[11px] text-[#7A6D66]">No shares yet.</li>}
            {shares.map((s) => (
              <li key={s.id} className="border border-[#E7DED7] rounded p-2.5 text-[11px]">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-data">{s.viewer_email}</span>
                  {s.revoked ? (
                    <Chip label="revoked" tone="bg-rose-100 text-rose-800" />
                  ) : (
                    <button
                      className="px-2 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid={`revoke-share-${s.id}`}
                      onClick={async () => {
                        await api.post(`/reports/shares/${s.id}/revoke`);
                        load();
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </div>
                <div className="font-data text-[#7A6D66] mt-1 break-all">
                  {s.url || "link destroyed"}
                </div>
                <div className="font-data text-[#7A6D66] mt-0.5">
                  expires {fmt(s.expires_at)} · views {s.views} · by {s.created_by}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-[#E7DED7] rounded-md bg-white p-4">
          <h3 className="font-console text-xs uppercase tracking-wider text-[#6B5F59]">
            Report access log
          </h3>
          <ul className="mt-3 space-y-1.5 max-h-72 overflow-y-auto console-scroll" data-testid="report-access-log">
            {logs.length === 0 && <li className="text-[11px] text-[#7A6D66]">No external access yet.</li>}
            {logs.map((l) => (
              <li key={l.id} className="text-[11px] font-data text-[#6B5F59] border-b border-[#E7DED7] pb-1">
                {fmt(l.at)} · {l.event} · {l.email} · {l.ip || "—"}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
